import { openPrintWindow } from "./printInvoice";
import { amountToWords } from "./numberToWords";

export interface PaymentVoucherData {
  farmName: string;
  voucherNumber: string;
  date: string;
  amount: number;
  paidTo: string;
  onAccountOf: string;
}

function esc(str: string): string {
  const el = document.createElement("span");
  el.textContent = str;
  return el.innerHTML;
}

function formatCurrency(n: number): string {
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function buildVoucherHTML(data: PaymentVoucherData): string {
  // Payment vouchers, like invoices, are to the nearest whole rupee (no paise).
  // Both the figure and the words derive from the same rounded value so they
  // can't disagree.
  const rupees = Math.round(data.amount);
  const words = amountToWords(rupees);

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Payment Voucher ${esc(data.voucherNumber)}</title>
  <style>
    @page { margin: 10mm; size: A4 portrait; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, Helvetica, sans-serif; color: #222; font-size: 13px; line-height: 1.5; }
    .voucher { width: 100%; max-width: 600px; margin: 20px auto; border: 2px solid #333; padding: 0; }
    .header { text-align: center; font-size: 18px; font-weight: 700; padding: 12px 16px 4px; letter-spacing: 1px; text-transform: uppercase; }
    .farm-name { text-align: center; font-size: 11px; color: #666; padding-bottom: 10px; border-bottom: 1px solid #ccc; }
    .body { padding: 16px 24px; }
    .row { display: flex; align-items: baseline; margin-bottom: 14px; }
    .label { font-weight: 600; min-width: 120px; flex-shrink: 0; font-size: 13px; }
    .value { flex: 1; border-bottom: 1px solid #999; padding-bottom: 2px; min-height: 18px; font-size: 13px; }
    .meta-row { display: flex; justify-content: space-between; margin-bottom: 14px; }
    .meta-item { display: flex; align-items: baseline; }
    .amount-value { font-size: 15px; font-weight: 700; }
    .signatures { display: flex; justify-content: space-between; margin-top: 40px; padding-top: 0; }
    .sig-block { text-align: center; width: 40%; }
    .sig-line { border-top: 1px solid #333; padding-top: 6px; font-size: 12px; font-weight: 600; }
  </style>
</head>
<body>
<div class="voucher">
  <div class="header">Payment Voucher</div>
  <div class="farm-name">${esc(data.farmName)}</div>

  <div class="body">
    <div class="meta-row">
      <div class="meta-item">
        <span class="label">PV No.</span>
        <span class="value" style="min-width:120px">${esc(data.voucherNumber)}</span>
      </div>
      <div class="meta-item">
        <span class="label" style="min-width:60px">Dated:</span>
        <span class="value" style="min-width:140px">${esc(data.date)}</span>
      </div>
    </div>

    <div class="row">
      <span class="label">Sum of Rupees:</span>
      <span class="value amount-value">\u20B9 ${formatCurrency(rupees)}</span>
    </div>

    <div class="row">
      <span class="label">In Words:</span>
      <span class="value" style="font-style:italic">${words ? esc(words) : ""}</span>
    </div>

    <div class="row">
      <span class="label">Paid To:</span>
      <span class="value">${data.paidTo ? esc(data.paidTo) : ""}</span>
    </div>

    <div class="row">
      <span class="label">On Account of:</span>
      <span class="value">${data.onAccountOf ? esc(data.onAccountOf) : ""}</span>
    </div>

    <div class="signatures">
      <div class="sig-block">
        <div class="sig-line">Prepared By</div>
      </div>
      <div class="sig-block">
        <div class="sig-line">Received By</div>
      </div>
    </div>
  </div>
</div>
</body>
</html>`;
}

export function printPaymentVoucher(data: PaymentVoucherData): void {
  const pw = openPrintWindow();
  if (!pw) return;

  const html = buildVoucherHTML(data);
  pw.document.open();
  pw.document.write(html);
  pw.document.close();

  setTimeout(() => {
    pw.focus();
    pw.print();

    const cleanup = () => pw.close();
    if ("onafterprint" in pw) {
      pw.onafterprint = cleanup;
    } else {
      setTimeout(cleanup, 2000);
    }
  }, 300);
}
